import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useSelector } from "react-redux";
import { addMinutes, isAfter, startOfHour, startOfToday } from "date-fns";
import { Button } from "primereact/button";
import { Calendar } from "primereact/calendar";
import { InputSwitch } from "primereact/inputswitch";
import { InputTextarea } from "primereact/inputtextarea";

import FormGroup from "../../common/components/form/FormGroup";
import { getTotalTimeInHoursAndMinutes } from "../../common/utils/dateHelper";
import { selectProfile } from "../auth/authSlice";
import { TicketTimeTracking } from "./TicketTimeTracking";
import { selectDepartmentById } from "../departments/departmentsSlice";
import { useAppSelector } from "../../common/hooks/useStore";
import TicketTimeTrackingTotalTimeLabel from "./TicketTimeTrackingTotalTimeLabel";

interface TicketTimeTrackingFormProps {
  departmentId: string;
  ticketId: string;
  ticketCompany: string;
  onSubmit: (ticketId: string, ticketTimeTracking: TicketTimeTracking) => Promise<void>;
}

export default function TicketTimeTrackingForm(props: TicketTimeTrackingFormProps) {
  const profile = useSelector(selectProfile);
  const department = useAppSelector((state) => selectDepartmentById(state, props.departmentId));
  const getDefaultValues = () => {
    const startTime = startOfHour(new Date());
    const endTime = addMinutes(startTime, 15);
    return {
      date: startOfToday().toString(),
      startTime: startTime.toString(),
      endTime: endTime.toString(),
      isBillable: props.ticketCompany !== profile.company,
      description: "",
      totalTime: getTotalTimeInHoursAndMinutes(startTime, endTime),
    };
  };
  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
    setValue,
  } = useForm<TicketTimeTracking>({ defaultValues: getDefaultValues() });
  const [isLoading, setIsLoading] = useState(false);

  const watchedStartTime = watch("startTime");
  const watchedEndTime = watch("endTime");
  const watchedTotalTime = watch("totalTime");

  useEffect(() => {
    if (watchedStartTime && watchedEndTime) {
      const totalTime = getTotalTimeInHoursAndMinutes(new Date(watchedStartTime), new Date(watchedEndTime));
      setValue("totalTime", totalTime);
    }
  }, [watchedStartTime, watchedEndTime]);

  const onSubmit = async (data: TicketTimeTracking) => {
    setIsLoading(true);
    await props.onSubmit(props.ticketId, data);
    reset(getDefaultValues());
    setIsLoading(false);
  };

  return (
    <form className="grid" onSubmit={handleSubmit(onSubmit)}>
      <FormGroup className="col-12 sm:col-4" label="Data" errorMessage={errors.date?.message}>
        <Controller
          name="date"
          control={control}
          rules={{ required: "Data este obligatorie" }}
          render={({ field }) => (
            <Calendar
              dateFormat="dd/mm/yy"
              value={field.value ? new Date(field.value) : null}
              onChange={(e) => field.onChange(e.value)}
            ></Calendar>
          )}
        ></Controller>
      </FormGroup>
      <FormGroup className="col-6 sm:col-4" label="Inceput" errorMessage={errors.startTime?.message}>
        <Controller
          name="startTime"
          control={control}
          rules={{ required: "Ora de inceput este obligatorie" }}
          render={({ field }) => (
            <Calendar
              timeOnly
              stepMinute={15}
              value={field.value ? new Date(field.value) : null}
              onChange={(e) => field.onChange(e.value)}
            ></Calendar>
          )}
        ></Controller>
      </FormGroup>
      <FormGroup className="col-6 sm:col-4" label="Final" errorMessage={errors.endTime?.message}>
        <Controller
          name="endTime"
          control={control}
          rules={{
            required: "Ora de sfarsit este obligatorie",
            validate: (value) =>
              !watchedStartTime ||
              isAfter(new Date(value), new Date(watchedStartTime)) ||
              "Ora finala trebuie sa fie mai mare decat ora de inceput",
          }}
          render={({ field }) => (
            <Calendar
              timeOnly
              stepMinute={15}
              value={field.value ? new Date(field.value) : null}
              onChange={(e) => field.onChange(e.value)}
            ></Calendar>
          )}
        ></Controller>
      </FormGroup>
      <FormGroup className="col-6" label="Total ore">
        <TicketTimeTrackingTotalTimeLabel
          className="flex align-items-center text-400 border-1 border-round-lg border-200 line-height-3 h-3rem p-2"
          hours={watchedTotalTime.hours}
          minutes={watchedTotalTime.minutes}
        />
      </FormGroup>
      <FormGroup className="col-6" label="Facturabil">
        <Controller
          name="isBillable"
          control={control}
          render={({ field }) => <InputSwitch checked={field.value} onChange={(e) => field.onChange(e.value)} />}
        ></Controller>
      </FormGroup>
      <FormGroup
        className="col-12"
        label={department?.timeTrackingNeedsDescription ? "Descriere *" : "Descriere"}
        errorMessage={errors.description?.message}
      >
        <Controller
          name="description"
          control={control}
          rules={{
            required: department?.timeTrackingNeedsDescription ? "Descrierea este obligatorie" : false,
          }}
          render={({ field }) => (
            <InputTextarea value={field.value} onChange={(e) => field.onChange(e.target.value)} rows={3} style={{ resize: "none" }} />
          )}
        ></Controller>
      </FormGroup>
      <Button type="submit" className="mb-4 ml-2" label="Adauga" icon="pi pi-plus" loading={isLoading} />
    </form>
  );
}
